/**
 * DEFUSE S16 — Properties panel single-select READ cutover (typed-first).
 *
 * The Properties palette single-select rows are the primary `EntityPreview`
 * string-bag READER (`derivePropertiesView` mapped `Object.entries(entity
 * .properties)` verbatim — the ONE place the whole bag reached the grid). This
 * helper flips the VALUE source of every descriptor-COVERED cell to the typed
 * `DrawingEntity` via `resolveDisplay(descriptor, typed)` — the same typed model
 * S15 already writes through — while keeping the bag ONLY for:
 *   - render-internal `__`-prefixed keys (unchanged filter — never user rows),
 *   - dynamic / indexed keys (`Vertex N X/Y`, `Param: …`, `MLENT_…`) owned by the
 *     typed vertex / block-parameter / mleader subsystems (no static descriptor),
 *   - `ui-projection-deferred` descriptors (Plot Style / Material / non-bearing
 *     Thickness — no typed home),
 *   - the NAMED `SINGLE_SELECT_BAG_RESIDUAL` set (dual-source decoders like Text
 *     "Justify" whose descriptor `display` diverges from the single-select
 *     builder, composite / BLOCK_RECORD-metadata Block rows, the NOT-YET-ROUTED
 *     Block `Hyperlink` override, the Multileader `String(...)` Arrow/Landing
 *     coords, the per-viewport view-descriptor subsystem),
 *   - the kind-independent `SINGLE_SELECT_COMMON_BAG_RESIDUAL` (Thickness /
 *     Linetype Scale — full-precision `String(...)` bag values the compact typed
 *     snapshot rounds or drops; kept bag-sourced on every kind),
 *   - the Float32 compact-read residual `isSingleSelectCompactNumericResidual`
 *     (every GEOMETRY / TRANSFORM numeric — Start X / Radius / angles / Length /
 *     Area — resolves through the Float32 compact snapshot, which rounds a
 *     full-precision coord; ALSO the `text` descriptors whose custom display
 *     FORMATS a Float32-backed numeric read: Viewport `Scale` and the serialized
 *     numeric / point arrays LWPOLYLINE `Vertices`/`Bulges` + Spline `Control
 *     points`/`Fit points`/`Knots`/`Weights`; kept bag-sourced until the read
 *     path is lossless).
 *
 * Row ORDER + membership are preserved exactly (still driven by the bag's key
 * order this slice), so the cutover is a pure per-cell source flip: for every
 * covered non-residual key `resolveDisplay(descriptor, typed) === the bag string`
 * (pinned BY CONSTRUCTION in `panelReadCutover.census.test.ts` + the S15
 * descriptor census), i.e. ZERO visible change. As descriptors expand the
 * residual can only shrink — the gate fails a new uncovered production key.
 *
 * Per ПРИОРИТЕТ #1 the typed classification (`SINGLE_SELECT_BAG_RESIDUAL`) is a
 * command-engine (CAD-core) fact; this module is the apps/web PROJECTION only.
 *
 * DEFUSE S16 remainder r1 (docs/MASTERPLAN.md Р16.5 W1.1) extends the same
 * cutover to the MULTI-select rows (`buildTypedMultiSelectPropertyValues`,
 * below) — see its doc comment for the per-row (not per-cell) eligibility rule
 * the multi-entity/varies computation requires.
 */

import {
  getTypedPropertyDescriptor,
  isSingleSelectCommonBagResidual,
  isSingleSelectCompactNumericResidual,
  resolveDisplay,
  resolveReadOnly,
  singleSelectBagResidual,
  type TypedPropertyDescriptor,
} from "@dxf-ai-editor/command-engine";
import type { EntityPreview, EntityType } from "@dxf-ai-editor/contracts";
import type { DrawingEntity } from "@dxf-ai-editor/drawing-model";

/**
 * Render-internal `EntityPreview.properties` keys are namespaced with a `__`
 * prefix (e.g. `__mtext_render_angle` from `buildMTextPreview`, the pre-existing
 * `__entity_annotative_contexts` / `__mleader_*` serialized-graph keys). They are
 * NOT user-facing fields — they carry derived render/preview signals the renderer
 * consumes, never values the Properties palette should display or the user can
 * edit. The multi-select path is allowlist-gated (`getSharedPropertiesForSelection`
 * never lists a `__`-key); the single-select path maps the bag verbatim, so this
 * is the ONE place these keys would leak into the grid as raw rows. Codex #342
 * round-8 P3. (Homed here — the single-select row builder is the sole consumer.)
 */
export function isInternalPropertyKey(key: string): boolean {
  return key.startsWith("__");
}

export type SingleSelectPropertyRow = {
  label: string;
  value: string;
  /** true when the displayed value was sourced from the typed `DrawingEntity`
   *  descriptor path (not the string bag) — asserted by the S16 census gate. */
  typedSourced: boolean;
  /** S16-ARRAYRECT-READONLY-ALIGN (W2.1) — true when the panel must render
   *  this cell non-interactive: an edit here would silently no-op at the
   *  write boundary (`resolveReadOnly`) — never offer a control the document
   *  cannot persist (ПРИОРИТЕТ #1). Independent of `typedSourced` (a
   *  bag-VALUE-sourced field can still be descriptor-gated read-only). */
  readOnly: boolean;
};

/**
 * The ONE eligibility check both the single-select (`buildTypedSingleSelectRows`)
 * and multi-select (`buildTypedMultiSelectPropertyValues`) row builders consult:
 * is THIS (kind, label) cell — on THIS typed entity — safe to source from the
 * typed `DrawingEntity` instead of the string bag? Returns the resolved
 * descriptor when eligible (so the caller doesn't re-look it up), `undefined`
 * otherwise. `typed === undefined` (entity not resolvable in the live doc, e.g.
 * a shell-only draft preview) is always ineligible — same degrade-to-bag rule
 * pre-cutover.
 *
 * Mirrors the exact per-cell test the S16 single-select census
 * (`panelReadCutover.census.test.ts`) pins byte-identical to the bag:
 *   - a descriptor exists and is not `ui-projection-deferred` (Plot Style /
 *     Material / non-bearing Thickness — no typed home),
 *   - the label is not in the NAMED per-kind residual (`SINGLE_SELECT_BAG_
 *     RESIDUAL` — dual-source decoders, BLOCK_RECORD metadata, viewport
 *     view-descriptors, …),
 *   - the label is not the kind-independent common-precision residual
 *     (Thickness / Linetype Scale),
 *   - the descriptor is not a Float32 compact-read residual (every GEOMETRY /
 *     TRANSFORM numeric resolves through the lossy compact snapshot).
 * One source of truth: a residual class added here automatically keeps the
 * corresponding multi-select cell bag-sourced too — no separate multi-select
 * residual set to drift out of sync (ПРИОРИТЕТ #1 — the classification already
 * lives in command-engine; this is just the single call site both builders share).
 */
function resolveTypedEligibleDescriptor(
  kind: EntityType,
  label: string,
  typed: DrawingEntity | undefined,
): TypedPropertyDescriptor | undefined {
  if (!typed) return undefined;
  const descriptor = getTypedPropertyDescriptor(kind, label);
  if (!descriptor) return undefined;
  if (descriptor.channel === "ui-projection-deferred") return undefined;
  if (singleSelectBagResidual(kind).has(label)) return undefined;
  // kind-independent precision / compact-drop residual (Thickness Float32,
  // Linetype Scale not round-tripped through the compact cache) — the typed
  // read path (`getEntityById`) cannot reproduce the bag's full-precision
  // `String(...)` projection, so keep them bag-sourced (Codex S16 R1).
  if (isSingleSelectCommonBagResidual(label)) return undefined;
  // Float32 compact-read residual: every GEOMETRY / TRANSFORM numeric (Start X,
  // Radius, angles, Length/Area calc, …) resolves through the Float32 compact
  // snapshot (`getEntityById` → `*FromCompact`), which rounds a full-precision
  // coord to a binary artifact — keep those bag-sourced (Codex S16 R3 P2).
  if (isSingleSelectCompactNumericResidual(descriptor, typed)) return undefined;
  return descriptor;
}

/**
 * S16-ARRAYRECT-READONLY-ALIGN (W2.1) — is (kind, label) a row the panel must
 * render READ-ONLY on `typed`? Independent of {@link resolveTypedEligibleDescriptor}
 * on purpose: a field can be VALUE-bag-sourced (e.g. every ArrayRect
 * numeric — a Float32 compact-read residual) while its EDITABILITY still
 * comes from the descriptor's `constraints.readOnly` predicate. Gated to
 * `channel === "editable-typed-field"` — every OTHER existing
 * `readOnly: true` descriptor in the registry (Block metadata / Plot Style /
 * non-bearing Thickness / …) already pairs it with a non-editable channel
 * (`read-only-calc` / `ui-projection-deferred`), so this predicate is
 * currently a NO-OP everywhere except ArrayRect `Associative` — it does not
 * retroactively grey out a field that already renders as an (silently
 * write-rejecting) editable input elsewhere. `typed === undefined`
 * (shell-only draft preview, nothing live to evaluate an entity-state
 * predicate against) degrades to editable, matching every other typed-read
 * degrade-to-bag rule in this module.
 */
function resolvePanelReadOnly(
  kind: EntityType,
  label: string,
  typed: DrawingEntity | undefined,
): boolean {
  if (!typed) return false;
  const descriptor = getTypedPropertyDescriptor(kind, label);
  if (!descriptor || descriptor.channel !== "editable-typed-field") return false;
  return resolveReadOnly(descriptor, typed);
}

/**
 * Build the single-select Properties-panel rows for one entity, sourcing every
 * descriptor-covered cell from the typed `DrawingEntity` and the residual from
 * the string bag. `typed === undefined` (entity not resolvable in the live doc,
 * e.g. a shell-only draft preview) degrades to the pre-cutover all-bag behaviour
 * — byte-identical output, never a regression.
 */
export function buildTypedSingleSelectRows(
  preview: EntityPreview,
  typed: DrawingEntity | undefined,
): SingleSelectPropertyRow[] {
  const rows: SingleSelectPropertyRow[] = [];
  for (const [label, bagValue] of Object.entries(preview.properties)) {
    // Strip render-internal `__` keys (matches the already-clean multi-select
    // allowlist path) — same filter as pre-cutover.
    if (isInternalPropertyKey(label)) continue;
    const descriptor = resolveTypedEligibleDescriptor(preview.type, label, typed);
    const typedSourced = descriptor !== undefined;
    rows.push({
      label,
      value: typedSourced ? resolveDisplay(descriptor, typed as DrawingEntity) : bagValue,
      typedSourced,
      readOnly: resolvePanelReadOnly(preview.type, label, typed),
    });
  }
  return rows;
}

/** One selected entity's `EntityPreview` (the bag / row-membership source) paired
 *  with its typed `DrawingEntity` resolution (`getEntityById` at the call site —
 *  `undefined` when the entity is not resolvable in the live doc). */
export type MultiSelectPropertySource = {
  preview: EntityPreview;
  typed: DrawingEntity | undefined;
};

export type MultiSelectPropertyValues = {
  label: string;
  /** Per-entity resolved value, same order/length as `entries`. In the
   *  bag-sourced case this is the RAW bag read (`preview.properties[label]`,
   *  possibly `undefined` — a property `getSharedPropertiesForSelection` admits
   *  via `editableProperties` but whose bag omits the key); the caller applies
   *  the pre-cutover `?? t("props.varies")` fallback before reducing to the
   *  displayed "same value or *Varies*" cell, exactly as it did on the bag path. */
  values: (string | undefined)[];
  /** true when EVERY entity in the row was sourced from its typed `DrawingEntity`
   *  — asserted by the multi-select census gate. */
  typedSourced: boolean;
  /** S16-ARRAYRECT-READONLY-ALIGN (W2.1) — true when EDITING this row would
   *  silently no-op for AT LEAST ONE selected entity (`resolvePanelReadOnly`
   *  per entity, OR-reduced). Conservative by design: a multi-select Apply
   *  writes the SAME value to every selected entity in one dispatch, so if
   *  even one entity would reject it the row must not present as a clean
   *  editable field (ПРИОРИТЕТ #1 — never offer an edit that partially fails
   *  silently). */
  readOnly: boolean;
};

/**
 * DEFUSE S16 remainder r1 — Properties-panel MULTI-select typed READ cutover.
 *
 * `derivePropertiesView`'s multi-select branch computes ONE row per property
 * `getSharedPropertiesForSelection` reports as common to every selected entity,
 * with a per-entity value the caller reduces via `variedValue` (same value on
 * every entity → that value; any divergence → "*Varies*"). Pre-cutover, every
 * per-entity value came from the string bag (`preview.properties[label]`); this
 * flips the VALUE source to the typed `DrawingEntity` — reusing the EXACT same
 * per-cell eligibility the single-select cutover already pins byte-identical to
 * the bag (`resolveTypedEligibleDescriptor`) — while keeping the residual on
 * the bag.
 *
 * Per row (label), sourcing is ALL-typed or ALL-bag, never a per-entity mix: if
 * entity A is typed-eligible but entity B degrades to the bag (residual,
 * deferred, or B's `typed === undefined`), comparing A's typed-resolved string
 * against B's stale bag string would manufacture a false "*Varies*" (or a false
 * agreement) that neither the old bag-only path nor a real AutoCAD multi-select
 * would show. So eligibility is computed per label across the WHOLE selection
 * first; only when every entity is individually eligible does the row source
 * every value from the typed model.
 *
 * Because eligibility is the identical predicate the single-select census
 * (`panelReadCutover.census.test.ts`) already proves resolves to the bag string
 * for every non-residual cell, a fully-typed-sourced row is byte-identical to
 * the pre-cutover bag row per entity — the ZERO-VISIBLE-CHANGE invariant carries
 * over as a corollary, pinned directly for the multi-entity/varies wiring by
 * `panelReadCutover.multiSelect.census.test.ts`.
 */
export function buildTypedMultiSelectPropertyValues(
  entries: readonly MultiSelectPropertySource[],
  sharedProperties: readonly string[],
): MultiSelectPropertyValues[] {
  return sharedProperties.map((label) => {
    const descriptors = entries.map(({ preview, typed }) =>
      resolveTypedEligibleDescriptor(preview.type, label, typed),
    );
    const typedSourced =
      descriptors.length > 0 && descriptors.every((descriptor) => descriptor !== undefined);
    const values = entries.map(({ preview, typed }, index) => {
      if (typedSourced) {
        return resolveDisplay(
          descriptors[index] as TypedPropertyDescriptor,
          typed as DrawingEntity,
        );
      }
      return preview.properties[label];
    });
    const readOnly = entries.some(({ preview, typed }) =>
      resolvePanelReadOnly(preview.type, label, typed),
    );
    return { label, values, typedSourced, readOnly };
  });
}
